import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "cb.recentPairs.v1";
const MAX_PAIRS = 8;

function safeRead() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((p) => p && typeof p.symbol === "string")
      .slice(0, MAX_PAIRS);
  } catch {
    return [];
  }
}

function safeWrite(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, MAX_PAIRS)));
  } catch {
    /* quota / privacy mode — ignore */
  }
}

export function useRecentPairs() {
  const [pairs, setPairs] = useState(() => safeRead());

  // Cross-tab sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setPairs(safeRead());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const record = useCallback((pair) => {
    if (!pair?.symbol) return;
    setPairs((prev) => {
      const now = Date.now();
      const filtered = prev.filter((p) => p.symbol !== pair.symbol);
      const next = [
        { symbol: pair.symbol, base: pair.base, quote: pair.quote, viewedAt: now },
        ...filtered,
      ].slice(0, MAX_PAIRS);
      safeWrite(next);
      return next;
    });
  }, []);

  const remove = useCallback((symbol) => {
    setPairs((prev) => {
      const next = prev.filter((p) => p.symbol !== symbol);
      safeWrite(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    safeWrite([]);
    setPairs([]);
  }, []);

  return { pairs, record, remove, clear };
}
